import { Route, Routes, Navigate } from "react-router-dom"
import { useAuthentication, RegisterUser, VerifyUser } from "@src/auth"
import { LoginScreen } from "@src/auth/LoginScreen"
import { DashboardScreen } from "@src/dashboard"
import { DevicesScreen } from "@src/device/DevicesScreen/DevicesScreen"
import { TheftReportScreen } from "@src/theftreport"
import { ImprintScreen, PrivacyPolicyScreen } from "@src/legal"
import { DeviceDetailScreen } from "@src/device/devicedetail/DeviceDetailScreen"
import { SelectPartnerScreen } from "@src/partner"
import { MapScreen } from "@src/maps"
import { EnergySaving } from "@src/energysave"
import {
  FleetsDetailScreen,
  FleetsScreen,
  PartnerSettingsScreen,
  UserSettingsScreen,
} from "@src/settings"
import { InviteUser } from "@src/invite"
import CreateFleetGeofenceScreen from "@src/settings/geofence/CreateFleetGeofenceScreen"
import EditFleetGeofenceScreen from "@src/settings/geofence/EditFleetGeofenceScreen"
import FleetGeofencesScreen from "@src/settings/geofence/SelectFleetScreen/FleetGeofencesScreen"

const usePrivateRoute = (): { privateComponent: (element: JSX.Element) => JSX.Element } => {
  const { isAuthenticated, tokenHasBeenLoaded } = useAuthentication()
  const privateComponent = (element: JSX.Element): JSX.Element => {
    return isAuthenticated || !tokenHasBeenLoaded ? element : <Navigate to="/login" replace />
  }
  return { privateComponent }
}

const ApplicationRouter = () => {
  const { isAuthenticated } = useAuthentication()
  const { privateComponent } = usePrivateRoute()

  return (
    <Routes>
      <Route
        path="/login"
        element={!isAuthenticated ? <LoginScreen /> : <Navigate to="/" replace />}
      />
      <Route path="/register" element={<RegisterUser />} />
      <Route path="/confirmation/:accessTokenId" element={<VerifyUser />} />
      <Route path="/" element={privateComponent(<SelectPartnerScreen />)} />
      <Route path="/invite/:acceptTokenId" element={<InviteUser />} />

      <Route path="/:partnerUuid" element={privateComponent(<DashboardScreen />)} />
      <Route path="/:partnerUuid/devices" element={privateComponent(<DevicesScreen />)} />
      <Route path="/:partnerUuid/map" element={privateComponent(<MapScreen />)} />
      <Route path="/:partnerUuid/theft" element={privateComponent(<TheftReportScreen />)} />
      <Route path="/:partnerUuid/energysaving" element={privateComponent(<EnergySaving />)} />

      <Route
        path="/:partnerUuid/theft/:serialnumber"
        element={privateComponent(<TheftReportScreen />)}
      />
      <Route
        path="/:partnerUuid/device/:serialnumber"
        element={privateComponent(<DeviceDetailScreen />)}
      />

      <Route
        path="/:partnerUuid/settings/user"
        element={privateComponent(<UserSettingsScreen />)}
      />
      <Route
        path="/:partnerUuid/settings/partner"
        element={privateComponent(<PartnerSettingsScreen />)}
      />
      <Route path="/:partnerUuid/settings/fleets" element={privateComponent(<FleetsScreen />)} />
      <Route
        path="/:partnerUuid/settings/fleets/:fleetUuid"
        element={privateComponent(<FleetsDetailScreen />)}
      />

      {/* settings/geofence */}
      <Route
        path="/:partnerUuid/settings/geofences"
        element={privateComponent(<FleetGeofencesScreen />)}
      />
      <Route
        path="/:partnerUuid/settings/geofences/:fleetUuid/create"
        element={privateComponent(<CreateFleetGeofenceScreen />)}
      />
      <Route
        path="/:partnerUuid/settings/geofences/:fleetUuid/:fenceUuid/edit"
        element={privateComponent(<EditFleetGeofenceScreen />)}
      />

      {/* general */}
      <Route path="/legal/imprint" element={<ImprintScreen />} />
      <Route path="/legal/privacy" element={<PrivacyPolicyScreen />} />
      <Route path="*" element={<p>There&apos;s nothing here: 404!</p>} />
    </Routes>
  )
}

export default ApplicationRouter
